import * as React from 'react'
import { INode } from 'shared/types/tree'
import { Box, Button } from '@mui/material'
import { PresentationSlidesView } from './SlidesView'
import { openNode } from '../../../utility/TreeNavigation'
import { NodeType } from 'shared/types/ingestion'
import { PotentiallyHtml } from '../../../PotentiallyHtml'
import { goToBuilder } from '../../TreeActions'
import { StudentFacingToggle } from '../../../utility/StudentFacingToggle'
import { ConfirmCommitButton } from 'shared/ui'
import { fixAssignablePresentation } from './utility'
import { TreePresentationsManager } from './TreePresentationsManager'
import { useUpdateStudentFacing } from '../../TreeRequests'

export function PresentationsStudentFacing({ tree }: { tree: INode }) {
  const [needFix, setNeedFix] = React.useState<INode[]>([])
  const [presentationManager, setPresentationManager] =
    React.useState<TreePresentationsManager>()

  React.useEffect(() => {
    const manager = new TreePresentationsManager(tree)
    setPresentationManager(manager)
    setNeedFix(manager.presentationFlagsIssues)
  }, [tree])

  function doFix(presentation: INode) {
    return fixAssignablePresentation(presentation).then(
      () =>
        presentationManager &&
        setNeedFix(presentationManager.presentationFlagsIssues),
    )
  }

  function fixAll() {
    if (!presentationManager) return
    return presentationManager
      .fixAllFlagIssues()
      .then(() => setNeedFix(presentationManager.presentationFlagsIssues))
  }

  return (
    <Box>
      <Box>Presentations Student Facing</Box>
      <Box>
        Out of {presentationManager?.getAssignablePresentations.length}{' '}
        assignable presentations {needFix.length} need updating.
      </Box>
      <ConfirmCommitButton onClick={fixAll}>
        <>Fix All</>
      </ConfirmCommitButton>
      <Box padding={'5px'}>
        {needFix.map((n) => (
          <Box key={n.id} padding={'5px'}>
            <Presentation presentation={n} fix={() => doFix(n)} />
          </Box>
        ))}
      </Box>
    </Box>
  )
}

function Presentation({
  presentation,
  fix,
}: {
  presentation: INode
  fix: () => unknown
}) {
  const [showDetails, setShowDetails] = React.useState(false)
  const [noteNodes, setNoteNodes] = React.useState<INode[]>([])
  const [disabled, setDisabled] = React.useState(false)
  const updateStudentFacing = useUpdateStudentFacing()

  React.useEffect(() => {
    setNoteNodes(presentation.nodes.filter((n) => n.type === NodeType.Note))
  }, [presentation])

  if (!showDetails) {
    return (
      <Box>
        <Button onClick={() => setShowDetails(true)}>
          {presentation.title}
        </Button>
        <ConfirmCommitButton onClick={fix}>
          <>Fix</>
        </ConfirmCommitButton>
      </Box>
    )
  }

  async function makeAllStudentFacing() {
    setDisabled(true)
    await updateStudentFacing(true, ...noteNodes)
    setDisabled(false)
  }

  return (
    <Box padding={'5px'} border={'solid 1px'}>
      <Box>
        <Button onClick={() => setShowDetails(false)}>
          {presentation.title}
        </Button>
        <StudentFacingToggle initialDisabled={disabled} node={presentation} />
        <ConfirmCommitButton
          style={{ color: 'darkorchid' }}
          variant={'outlined'}
          onClick={makeAllStudentFacing}
          disabled={disabled}
        >
          <>Make Presentation And All Notes Student Facing</>
        </ConfirmCommitButton>
      </Box>
      <Box>
        <Button onClick={() => openNode(presentation)}>Open In Kiddom</Button>
        <Button
          onClick={() =>
            goToBuilder({
              treeId: presentation.tree_id,
              versionId: presentation.version,
              nodeId: presentation.id,
            })
          }
        >
          Open Builder
        </Button>
      </Box>
      <PresentationSlidesView presentation={presentation} />
      <PresentationNotes noteNodes={noteNodes} />
    </Box>
  )
}

function PresentationNotes({ noteNodes }: { noteNodes: INode[] }) {
  if (!noteNodes.length) return null

  return (
    <Box>
      <Box>Notes</Box>
      {noteNodes.map((n) => {
        return (
          <Box key={n.id} padding={'5px'} border={'solid 1px'}>
            <Box>
              <StudentFacingToggle node={n} />
            </Box>
            <PotentiallyHtml s={n.body?.value} />
          </Box>
        )
      })}
    </Box>
  )
}
